import { useEffect, useState } from 'react';
import type { NodeStyle } from '../../domain';
import { NodeMenuId } from './NodeMenuId';
import { NodeMenuContent } from './NodeMenuContent';
import { MenuContainer } from '@model-framework/menu';
import { MenuSize } from '@view-model/models/sticky/ui';
import type { ThemeColor } from '@view-model/models/common/color';
import type { FontSize } from '@model-framework/text';
import type { TextAlign } from '@view-model/models/common/text-align';
import type { UserId } from 'balus-common';
import type { NodeReaction, ReactionType } from '@view-model/models/sticky/NodeReaction';
import { Point } from '@view-model/models/common/basic';

type Props = {
  currentStyle: NodeStyle;
  offset: { x: number; y: number };
  textEditing: boolean;
  url: string | null;
  onFontSizeSelected(fontSize: FontSize): void;
  onToggleFontStrikethrough(): void;
  onThemeColorSelected(themeColor: ThemeColor): void;
  onTextAlignSelected(textAlign: TextAlign): void;
  onDeleteNode(): void;
  onToggleNodeDescription(): void;
  onGroupSelectedNode(): void;
  onSaveHyperLink(url: string | null): void;
  reaction: NodeReaction | null | undefined;
  toggleReaction: (reactionName: ReactionType, userId: UserId, userName: string) => void;
};

export const NodeMenu: React.FC<Props> = ({
  currentStyle,
  textEditing,
  url,
  offset,
  onFontSizeSelected,
  onToggleFontStrikethrough,
  onThemeColorSelected,
  onTextAlignSelected,
  onDeleteNode,
  onToggleNodeDescription,
  onGroupSelectedNode,
  onSaveHyperLink,
  reaction,
  toggleReaction,
}: Props) => {
  const [openedMenu, setOpenedMenu] = useState<NodeMenuId | null>(null);

  useEffect(() => {
    if (textEditing) {
      setOpenedMenu(null);
    }
  }, [textEditing]);

  const availableMenuIds = NodeMenuId.values();

  const menuSize = { width: MenuSize * availableMenuIds.length, height: MenuSize };
  const position = new Point(offset.x, offset.y);

  return (
    <MenuContainer position={position} size={menuSize} positionOrigin="left-bottom">
      {availableMenuIds.map((menuId, i) => (
        <g key={menuId} transform={`translate(${MenuSize * i}, 0)`}>
          <NodeMenuContent
            menuId={menuId}
            menuSize={MenuSize}
            currentStyle={currentStyle}
            isOpen={openedMenu === menuId}
            url={url}
            reaction={reaction}
            onMenuOpen={() => setOpenedMenu(menuId)}
            onMenuClose={() => setOpenedMenu(null)}
            onFontSizeSelected={onFontSizeSelected}
            onToggleFontStrikethrough={onToggleFontStrikethrough}
            onThemeColorSelected={onThemeColorSelected}
            onTextAlignSelected={onTextAlignSelected}
            onDeleteNode={onDeleteNode}
            onGroupSelectedNode={onGroupSelectedNode}
            onToggleNodeDescription={onToggleNodeDescription}
            onSaveHyperLink={onSaveHyperLink}
            onReactionSelected={toggleReaction}
          />
        </g>
      ))}
    </MenuContainer>
  );
};
